import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Gauge, ChevronDown, ChevronUp } from "lucide-react";

/**
 * TrustScoreBadge — 0-100 composite the rest of the platform sorts by.
 *
 * Props:
 *   - score (number) — pre-computed score (from /providers/discover etc.)
 *   - providerId (string) — if passed without `score`, fetches /trust/score/:id
 *   - compact (boolean) — chip-only (no expander)
 *   - showBreakdown (boolean) — eager-expand the per-component breakdown
 */
const SCORE_COLORS = (s) => {
  if (s >= 75) return { ring: "text-secondary", bg: "bg-secondary/15", text: "text-secondary" };
  if (s >= 50) return { ring: "text-violet-300", bg: "bg-violet-400/10", text: "text-violet-200" };
  if (s >= 25) return { ring: "text-blue-300", bg: "bg-blue-400/10", text: "text-blue-200" };
  return { ring: "text-foreground/55", bg: "bg-foreground/10", text: "text-foreground/65" };
};

const LABELS = {
  tierBase: "Verified tier",
  reviews: "Reviews",
  endorsements: "Endorsements",
  responseTime: "Response time",
  disputes: "Dispute history",
  volume: "Booking volume",
};

export default function TrustScoreBadge({ score: scoreProp, providerId, compact = false, showBreakdown = false }) {
  const [data, setData] = useState(scoreProp != null ? { score: scoreProp } : null);
  const [open, setOpen] = useState(showBreakdown);
  const needsFetch = scoreProp == null || showBreakdown;

  useEffect(() => {
    if (!needsFetch || !providerId) return;
    api.get(`/trust/score/${providerId}`).then(({ data }) => setData(data)).catch(() => {});
  }, [providerId, needsFetch]);

  if (!data || data.score == null) return null;
  const s = data.score;
  const c = SCORE_COLORS(s);

  if (compact) {
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${c.bg} ${c.text} text-[10px] font-medium tracking-wider`}
        data-testid={`trust-score-badge-${providerId || "x"}`}
        title={`Trust Score · ${s}/100`}
      >
        <Gauge size={10} strokeWidth={2} />
        {s}
      </span>
    );
  }

  return (
    <div
      className="inline-block w-full"
      data-testid={`trust-score-block-${providerId || "x"}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-sm ${c.bg} ${c.text}`}
      >
        <Gauge size={14} strokeWidth={1.5} />
        <span className="text-[11px] uppercase tracking-[0.18em]">Trust Score</span>
        <span className="font-serif text-xl leading-none">{s}</span>
        <span className="text-foreground/40 text-[10px]">/100</span>
        {data.breakdown && (open ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
      </button>

      {open && data.breakdown && (
        <div className="mt-2 border border-secondary/15 rounded-sm p-3 space-y-1.5" data-testid="trust-score-breakdown">
          {Object.entries(data.breakdown).map(([k, v]) => (
            <div key={k} className="flex items-center justify-between text-[11px]">
              <span className="text-foreground/65">{LABELS[k] || k}</span>
              <span className={v >= 0 ? "text-secondary/90" : "text-orange-300"}>
                {v > 0 ? "+" : ""}{v}
              </span>
            </div>
          ))}
          {data.metrics?.responseMedianHours != null && (
            <div className="text-[10px] text-foreground/45 pt-1 border-t border-secondary/10">
              Median response: {data.metrics.responseMedianHours}h · {data.metrics.reviewCount} reviews · {data.metrics.endorsementCount} endorsements · {data.metrics.totalBookings} bookings
            </div>
          )}
        </div>
      )}
    </div>
  );
}
